import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@apollo/client';
import {
  GET_POD_EXEMPT_DICT_DISPLAY_ITEMS,
  GET_POD_EXEMPT_DICT_ITEMS,
  PodExemptDictDisplayItemsResponse,
  PodExemptDictItemsResponse,
} from '../graphql/podExempt/queries';
import {
  dictDisplayName,
  parseDictExt,
  PodExemptDictExt,
  PodExemptDictItemType,
  PodExemptDictType,
} from '../graphql/podExempt/types';

export interface PodExemptDictOption {
  code: string;
  /** 按界面语言在前端解析（见 dictDisplayName），不用后端 Accept-Language 那份 */
  name: string;
  parentCode: string | null;
  ext: PodExemptDictExt;
}

/**
 * 门牌免拍字典 hook（分类体系纯配置：产品未配置地址类型时 addressType 列表为空，
 * 提交页应隐藏分类选择、原因平铺展示——调用方按 options.length 判断，零发版兼容）。
 */
export function usePodExemptDict(dictType: PodExemptDictType, parentCode?: string | null) {
  // 字典名按界面语言在前端取，不用后端按 Accept-Language 解析的 name（见 dictDisplayName 注释）
  const {i18n} = useTranslation();
  const {data, loading, error, refetch} = useQuery<PodExemptDictItemsResponse>(
    GET_POD_EXEMPT_DICT_ITEMS,
    {
      variables: {dictType, parentCode: parentCode ?? null},
      // cache-and-network：字典停用/新增后，上报抽屉重新打开即拿到最新项（后端提交时仍校验启用兜底）
      fetchPolicy: 'cache-and-network',
    },
  );

  // 全量映射（含停用与已删）：历史单据回显 code→名称；表单可选项仍用启用口径的 options
  const {data: displayData} = useQuery<PodExemptDictDisplayItemsResponse>(
    GET_POD_EXEMPT_DICT_DISPLAY_ITEMS,
    {variables: {dictType}, fetchPolicy: 'cache-and-network'},
  );

  const options: PodExemptDictOption[] = useMemo(
    () =>
      (data?.podExemptDictItems ?? []).map((item: PodExemptDictItemType) => ({
        code: item.code,
        name: dictDisplayName(item, i18n.language),
        parentCode: item.parentCode,
        ext: parseDictExt(item.extJson),
      })),
    [data, i18n.language],
  );

  const items: PodExemptDictOption[] = useMemo(
    () =>
      (displayData?.podExemptDictDisplayItems ?? []).map((item: PodExemptDictItemType) => ({
        code: item.code,
        name: dictDisplayName(item, i18n.language),
        parentCode: item.parentCode,
        ext: parseDictExt(item.extJson),
      })),
    [displayData, i18n.language],
  );

  return {options, items, loading, error, refetch};
}
